import { useEffect, useRef } from 'react';

import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { TextContentData } from '../../../types/contentTypes';

import './TextContent.scss';

interface TextContentProps {
  content: TextContentData;
  onComplete: (() => void) | undefined;
}

export default function TextContent({ content, onComplete }: TextContentProps) {
  const textDiv = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function correctSubmitIfScrolledIntoView() {
      if (textDiv.current) {
        const rect = textDiv.current.getBoundingClientRect();
        const elemBottom = rect.bottom;

        const isVisible = elemBottom <= window.innerHeight;

        if (isVisible) {
          window.removeEventListener('scroll', correctSubmitIfScrolledIntoView);
          if (onComplete) {
            onComplete();
            return true;
          }
        }
      }
      return false;
    }

    const submitted = correctSubmitIfScrolledIntoView();

    if (!submitted) {
      window.addEventListener('scroll', correctSubmitIfScrolledIntoView);
    }
  }, [onComplete]);

  return (
    <section className="text-content">
      <h2 className="strong-heading mb-8">{content.headline}</h2>
      <div ref={textDiv}>
        <ReactMarkdown
          className="markdown-text"
          disallowedElements={['h1']}
          components={{
            code({ node, inline, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <SyntaxHighlighter
                  // @ts-ignore -- Don't know why TS is complaining, follows the example
                  style={prism}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            },
          }}
        >
          {content.text}
        </ReactMarkdown>
      </div>
    </section>
  );
}
